import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../config/api";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Upload, FileText, CheckCircle, Clock, Loader2,
  Download, Send, PoundSterling, AlertCircle, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import FileUploadZone from "../components/FileUploadZone";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import TestEnvBanner from "../components/TestEnvBanner";
import { format, parseISO } from "date-fns";
import { getUserRole, ROLES } from "../config/roleConfig";

// Users who can upload invoices
const UPLOADER_EMAILS = [
  "vanessa@bonsai.law",
  "rebekah@bonsai.law",
  "liezel@bonsai.law",
  "clientcare@bonsai.law",
];
const CASHIER_EMAIL = "may@bonsai.law";

function getHeaders() {
  const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
  return userId ? { "X-Bonsai-User-Id": userId } : {};
}

function getUserEmail() {
  const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return userId.includes("@") ? userId.toLowerCase() : "";
}

const ACTION_OPTIONS = [
  { value: "pay_immediately", label: "Pay immediately" },
  { value: "check_records", label: "Please check records and annotate" },
  { value: "pay_in_days", label: "Pay in X days", hasCustomDays: true },
];

const ACTION_PRESETS = [
  { value: "7", label: "Pay in 7 days" },
  { value: "14", label: "Pay in 14 days" },
];

const STATUS_BADGES = {
  pending: { bg: "bg-status-amber/15", text: "text-status-amber", label: "Pending" },
  in_progress: { bg: "bg-blue-500/15", text: "text-blue-300", label: "In Progress" },
  done: { bg: "bg-status-green/15", text: "text-status-green", label: "Done" },
};

const ACTION_LABELS = {
  pay_immediately: "Pay immediately",
  check_records: "Check records & annotate",
  pay_in_days: "Pay in days",
};

// ── Upload Form ──

function UploadForm({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [supplierName, setSupplierName] = useState("");
  const [matterReference, setMatterReference] = useState("");
  const [clientName, setClientName] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [action, setAction] = useState("");
  const [payInDays, setPayInDays] = useState("");
  const [uploading, setUploading] = useState(false);

  const effectiveAction = action.startsWith("preset_")
    ? "pay_in_days"
    : action === "pay_in_days_custom"
      ? "pay_in_days"
      : action;

  const effectiveDays = action === "preset_7"
    ? 7
    : action === "preset_14"
      ? 14
      : action === "pay_in_days_custom"
        ? parseInt(payInDays, 10) || 0
        : null;

  const canSubmit =
    file &&
    supplierName.trim() &&
    effectiveAction &&
    (effectiveAction !== "pay_in_days" || (effectiveDays && effectiveDays > 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("supplier_name", supplierName.trim());
      formData.append("action", effectiveAction);
      if (matterReference) formData.append("matter_reference", matterReference);
      if (clientName) formData.append("client_name", clientName);
      if (amount) formData.append("amount", parseFloat(amount));
      if (description.trim()) formData.append("description", description.trim());
      if (effectiveAction === "pay_in_days") formData.append("pay_in_days", effectiveDays);

      await axios.post(`${API}/hub/third-party-invoices`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });

      toast.success("Invoice uploaded — May has been notified");
      setFile(null);
      setSupplierName("");
      setAmount("");
      setDescription("");
      setAction("");
      setPayInDays("");
      onUploaded();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to upload invoice"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="glass-card rounded-lg p-6 border border-teal/20 mb-8">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Upload className="w-5 h-5 text-teal" />
        Upload Third-Party Invoice
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Supplier Name */}
        <div>
          <label className="block text-sm font-medium text-txt-muted mb-1.5">
            Supplier Name <span className="text-status-red">*</span>
          </label>
          <Input
            value={supplierName}
            onChange={(e) => setSupplierName(e.target.value)}
            placeholder="e.g. Land Registry, HMRC, Counsel"
            className="bg-navy-hover border-navy-border text-white placeholder:text-txt-muted"
          />
        </div>

        {/* Client / Matter */}
        <div>
          <SmartClientMatterSearch
            label="Client / Matter"
            placeholder="Search client or matter..."
            onSelect={(result) => {
              if (result.matter_reference) setMatterReference(result.matter_reference);
              if (result.client_name) setClientName(result.client_name);
            }}
            darkMode={true}
          />
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-txt-muted mb-1.5">
            Amount (optional)
          </label>
          <div className="relative">
            <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-muted" />
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="bg-navy-hover border-navy-border text-white placeholder:text-txt-muted pl-9"
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-txt-muted mb-1.5">
          Description (optional)
        </label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief note about the invoice"
          className="bg-navy-hover border-navy-border text-white placeholder:text-txt-muted"
        />
      </div>

      {/* Action */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-txt-muted mb-1.5">
          Action Required <span className="text-status-red">*</span>
        </label>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="bg-navy-hover border-navy-border text-white">
            <SelectValue placeholder="Select action for May..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pay_immediately">Pay immediately</SelectItem>
            <SelectItem value="check_records">Please check records and annotate</SelectItem>
            <SelectItem value="preset_7">Pay in 7 days</SelectItem>
            <SelectItem value="preset_14">Pay in 14 days</SelectItem>
            <SelectItem value="pay_in_days_custom">Pay in X days (custom)</SelectItem>
          </SelectContent>
        </Select>
        {action === "pay_in_days_custom" && (
          <div className="mt-2">
            <Input
              type="number"
              min="1"
              value={payInDays}
              onChange={(e) => setPayInDays(e.target.value)}
              placeholder="Number of days"
              className="bg-navy-hover border-navy-border text-white placeholder:text-txt-muted w-48"
            />
          </div>
        )}
      </div>

      {/* File Upload */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-txt-muted mb-1.5">
          Invoice File <span className="text-status-red">*</span>
        </label>
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onRemove={() => setFile(null)}
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
          label="Drop invoice here or click to browse"
          hint="PDF or image files (max 15 MB)"
          uploading={uploading}
        />
      </div>

      <Button
        type="submit"
        disabled={!canSubmit || uploading}
        className="bg-teal hover:bg-teal-hover text-white gap-2 font-bold"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            Submit Invoice
          </>
        )}
      </Button>
    </form>
  );
}

// ── Invoice List (shared by uploader history + May's view) ──

function InvoiceList({ invoices, isCashier, onStatusUpdate, updatingId }) {
  const handleDownload = async (invoice) => {
    try {
      const res = await axios.get(
        `${API}/hub/third-party-invoices/${invoice.id}/download`,
        { headers: getHeaders() }
      );
      const { file_name, file_content_type, file_data } = res.data;
      const byteChars = atob(file_data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: file_content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download file");
    }
  };

  if (!invoices.length) {
    return (
      <div className="text-center py-12 text-txt-muted">
        <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No invoices found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {invoices.map((inv) => {
        const badge = STATUS_BADGES[inv.status] || STATUS_BADGES.pending;
        const actionLabel =
          inv.action === "pay_in_days"
            ? `Pay in ${inv.pay_in_days} days`
            : ACTION_LABELS[inv.action] || inv.action;

        return (
          <InvoiceCard
            key={inv.id}
            invoice={inv}
            badge={badge}
            actionLabel={actionLabel}
            isCashier={isCashier}
            onStatusUpdate={onStatusUpdate}
            onDownload={() => handleDownload(inv)}
            isUpdating={updatingId === inv.id}
          />
        );
      })}
    </div>
  );
}

function InvoiceCard({ invoice, badge, actionLabel, isCashier, onStatusUpdate, onDownload, isUpdating }) {
  const [note, setNote] = useState(invoice.may_note || "");
  const [showNote, setShowNote] = useState(false);

  const handleMarkDone = () => {
    onStatusUpdate(invoice.id, "done", note.trim() || null);
  };

  const handleSaveNote = () => {
    onStatusUpdate(invoice.id, null, note.trim());
  };

  return (
    <div className="glass-card rounded-lg p-4 border border-teal/10 hover:border-teal/25 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="font-bold text-white truncate">{invoice.supplier_name}</h4>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
              {badge.label}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-txt-muted">
            {invoice.amount != null && (
              <span className="flex items-center gap-1">
                <PoundSterling className="w-3 h-3" />
                {invoice.currency} {invoice.amount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {format(parseISO(invoice.uploaded_at), "dd MMM yyyy HH:mm")}
            </span>
            <span>By: {invoice.uploaded_by}</span>
            {invoice.file_name && (
              <span className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {invoice.file_name}
              </span>
            )}
          </div>

          {/* Action instruction — prominent */}
          <div className="mt-2 px-3 py-1.5 rounded bg-status-amber/10 border border-amber-500/20 inline-flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-status-amber flex-shrink-0" />
            <span className="text-sm font-medium text-status-amber">{actionLabel}</span>
          </div>

          {invoice.description && (
            <p className="mt-2 text-xs text-txt-muted italic">{invoice.description}</p>
          )}

          {invoice.may_note && !showNote && (
            <p className="mt-2 text-xs text-txt-muted bg-navy-hover px-3 py-2 rounded">
              <span className="font-bold text-teal">May&apos;s note:</span> {invoice.may_note}
            </p>
          )}

          {invoice.done_at && (
            <p className="mt-1 text-xs text-status-green">
              Completed: {format(parseISO(invoice.done_at), "dd MMM yyyy HH:mm")}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onDownload}
            className="border-teal/30 text-teal hover:bg-teal/20 gap-1.5 text-xs"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </Button>

          {isCashier && invoice.status !== "done" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNote(!showNote)}
                className="border-blue-400/30 text-blue-300 hover:bg-blue-500/10 gap-1.5 text-xs"
              >
                <ClipboardCheck className="w-3.5 h-3.5" />
                {showNote ? "Hide Note" : "Add Note"}
              </Button>
              <Button
                size="sm"
                onClick={handleMarkDone}
                disabled={isUpdating}
                className="bg-status-green hover:bg-status-green text-white gap-1.5 text-xs font-bold"
              >
                {isUpdating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CheckCircle className="w-3.5 h-3.5" />
                )}
                Mark Done
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Note input area for May */}
      {showNote && isCashier && invoice.status !== "done" && (
        <div className="mt-3 pt-3 border-t border-navy-border">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note or annotation..."
            rows={2}
            className="w-full bg-navy-hover border border-navy-border rounded-md px-3 py-2 text-sm text-white placeholder:text-txt-muted focus:outline-none focus:border-teal/50"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveNote}
            disabled={isUpdating}
            className="mt-2 border-blue-400/30 text-blue-300 hover:bg-blue-500/10 text-xs"
          >
            Save Note
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──

export default function ThirdPartyInvoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [updatingId, setUpdatingId] = useState(null);

  const userEmail = getUserEmail();
  const userRole = getUserRole(userEmail);
  const isCashier = userEmail === CASHIER_EMAIL || userRole === ROLES.DIRECTOR;
  const isUploader = UPLOADER_EMAILS.includes(userEmail) || isCashier;

  const fetchInvoices = useCallback(async () => {
    try {
      const endpoint =
        tab === "pending"
          ? `${API}/hub/third-party-invoices/pending`
          : `${API}/hub/third-party-invoices?status=${tab === "done" ? "done" : ""}`;

      const res = await axios.get(endpoint, { headers: getHeaders() });
      setInvoices(res.data.invoices || []);
    } catch (err) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    fetchInvoices();
  }, [fetchInvoices]);

  const handleStatusUpdate = async (invoiceId, status, mayNote) => {
    setUpdatingId(invoiceId);
    try {
      const body = {};
      if (status) body.status = status;
      if (mayNote !== undefined && mayNote !== null) body.may_note = mayNote;

      await axios.patch(`${API}/hub/third-party-invoices/${invoiceId}`, body, {
        headers: getHeaders(),
      });

      if (status === "done") {
        toast.success("Invoice marked as done");
      } else {
        toast.success("Note saved");
      }
      fetchInvoices();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update invoice"));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy to-navy">
      <TestEnvBanner />
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50 w-full border-b border-teal/15">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-txt-muted hover:text-white p-1"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-teal" />
                Third-Party Invoices
              </h1>
              <p className="text-xs text-txt-muted">
                {isCashier
                  ? "Review and process supplier invoices"
                  : "Upload invoices for May to process"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Upload form for authorised uploaders */}
        {isUploader && <UploadForm onUploaded={fetchInvoices} />}

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          {["pending", "done", "all"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t
                  ? "bg-teal text-white"
                  : "text-txt-muted hover:text-white hover:bg-navy-hover"
              }`}
            >
              {t === "pending" && "Pending"}
              {t === "done" && "Done"}
              {t === "all" && "All"}
            </button>
          ))}
        </div>

        {/* Invoice list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-teal animate-spin" />
          </div>
        ) : (
          <InvoiceList
            invoices={invoices}
            isCashier={isCashier}
            onStatusUpdate={handleStatusUpdate}
            updatingId={updatingId}
          />
        )}
      </div>
    </div>
  );
}
